import { showWarningDialog } from "#skyward_modalLayer";
import { encodeHtml } from "./skyward.base.extensions";
import { getContentMessageArea } from "./skyward.ui.contentMessageArea";
import { getOriginalValue, getValue, setValue } from "./skyward.ui.controls";
import { disableButtons, enableButtons } from "./skyward.ui.dataobjectviewer.getFieldValues";
import { changeTracking as dmseChangeTracking } from "./skyward.ui.distinctMultiSelectEEL";
import { classNames as validationClassNames, getControl, getHelpMessage, getHtmlViewers, getLabel, setAriaInvalid } from "./skyward.ui.validation";
import { JQueryInput } from "./types";

export interface ChangeTracker
{
    isDirty: () => boolean;
    overrideDirtyState: (state: boolean | undefined) => void;
    clearDirtyList: (resetValues?: boolean) => void;
}

const skyward = window.skyward;

const classnames = validationClassNames;

let globalDirtyOverride: boolean | undefined;


export function isDirty(selectorArg?: JQueryInput)
{
    if (typeof globalDirtyOverride === "boolean")
    {
        return globalDirtyOverride;
    }

    if (selectorArg)
    {
        const selector = $(selectorArg);
        const override = selector.data("dirty-override");
        if (typeof override === "boolean")
        {
            return override;
        }
        return containerHasDirtyFields(selector);
    }

    return !!document.querySelector("[data-dirty=\"true\"]") ||
        $(".js-changeTrackingRoot").toArray().some(function (root)
        {
            return $(root).data("dirty-override");
        });
}

/**
 * Override the dirty states for the entire page, or reset it by passing undefined.  Note that this will
 * not be cleared automatically, and so it is unlikely to do what you want if your page is displayed in
 * a details panel, for example. Prefer using getChangeTracker().overrideDirtyState() whenever possible.
 * @param override
 */
export function overrideGlobalDirtyState(override?: boolean)
{
    globalDirtyOverride = override;
}

export function clearDirtyList(dovId?: string, frameWindow: Window = window, resetValues = false)
{
    const selector = dovId ? $(`#${dovId}`, frameWindow.document) : $(frameWindow.document.body);

    return clearDirtyListInternal(selector, resetValues);
}

export function removeFromDirtyList(dirtyValuesToRemove: JQueryInput[])
{
    $.each(dirtyValuesToRemove, function ()
    {
        $(this).attr("data-dirty", "false");
    });
}

export function getChangeTracker(selectorArg: JQueryInput)
{
    const selector = $(selectorArg);
    const root = selector.closest(".js-changeTrackingRoot");
    if (root.length === 0)
    {
        return undefined;
    }
    const changeTracker: ChangeTracker =
    {
        isDirty: function () { return isDirty(root); },
        overrideDirtyState: function (override) { overrideDirtyState(root, override); },
        clearDirtyList: function (resetValues = false) { return clearDirtyListInternal(root, resetValues); }
    };
    return changeTracker;
}

export function showDirtyScreenModal(onConfirm?: () => void)
{
    return showWarningDialog(
        "leave this record",
        "You have unsaved data that will be lost if you leave this record.",
        {
            onConfirm
        });
}

export function showDirtyScreenModalAsync()
{
    return showDirtyScreenModal().then(() => true, () => false);
}

export function setUpChangeTracking(selector: JQueryInput, optionalCallback?: (changeTrackingRoot: JQuery<HTMLElement>, input: JQuery<HTMLElement>, dirty: boolean) => void)
{
    const callback = typeof optionalCallback === "function" ? optionalCallback : toggleDovDirtyState;
    $(selector).each(function ()
    {
        const changeTrackingRoot = $(this);

        changeTrackingRoot.on("notifyDirty", function (event)
        {
            const element = $(event.target);
            const dirty = element.attr("data-dirty") === "true";
            callback(changeTrackingRoot, element, dirty);
        });
        changeTrackingRoot.on(
            "change dirty keyup", "[data-original-value]:not([excludefromdataobjectviewer], .browseSearchBox, .browseRowsPerPage)",
            function (event: JQuery.TriggeredEvent<HTMLElement, undefined, any, any> & { trackedChanges?: boolean })
            {
                //prevent nested changetracking containers from triggering multiple times
                if (event.trackedChanges)
                {
                    return;
                }
                event.trackedChanges = true;
                const element = $(this);
                let value = getValue(element);
                let originalValue = getOriginalValue(element);
                const overrideDirtyStringLogic = element.is("[override-dirty-string-logic]");
                const ignoreDirtyComparison = element.is("[ignoredirtyfieldcomparison]");

                if (element.hasClass('multiSelectBackingData'))
                {
                    value = value.length === originalValue.length && $(value).not(originalValue).length === 0 && $(originalValue).not(value).length === 0;
                    originalValue = true;
                }
                else if (element.hasClass('dmsBackingData'))
                {
                    value = dmseChangeTracking(element, value, originalValue);
                    originalValue = true;
                }
                // TOOD: Remove in PR 6145635
                else if (element.hasClass("datepicker") && !element.hasClass("relativeDate"))
                {
                    // For datepicker changetracking, we care about the literal text, not the Date value.
                    value = (element.val() as string).substring(0, 10).trim().replace(/\b0/g, "");
                    originalValue = (element.data("original-value")?.substring(0, 10)) ?? element.data("originalRelativeDate") ?? "";
                    originalValue = originalValue.trim().replace(/\b0/g, "");
                }
                else if (!overrideDirtyStringLogic && (element.is("input[type=number],input[type=range]") || element.is("[data-decimal-mask].masked")))
                {
                    value = normalizeNumberString(value);
                    originalValue = normalizeNumberString(originalValue);
                }
                else
                {
                    value = stringify(value) ?? "";
                    originalValue = encodeHtml(stringify(originalValue) ?? "");
                }

                let dirty;
                if (element.hasClass("ckeditor"))
                {
                    dirty = window.CKEDITOR.instances[element.attr("id")!].checkDirty();
                }
                else if (ignoreDirtyComparison)
                {
                    dirty = false;
                }
                else
                {
                    dirty = value !== originalValue;
                }
                // Vue custom elements (sky-timepicker, etc.) use a MutationObserver that
                // observes ALL attribute changes, including non-prop attributes like data-dirty.
                // This triggers a re-render that resets the inner input to props.value while the
                // user is still typing (during keyup, props.value hasn't been updated yet).
                // Set data-dirty on the inner input instead to avoid this during keyup.
                if (element.is("sky-timepicker,sky-datepicker,sky-datetimepicker"))
                {
                    element.find(":input").first().attr("data-dirty", dirty.toString());
                }
                else
                {
                    element.attr("data-dirty", dirty.toString());
                }

                callback(changeTrackingRoot, element, dirty);
            });
    });
}

export function setIgnoreDirtyFieldComparison(selector: JQueryInput, value: boolean)
{
    const element = $(selector);
    element.attr("ignoredirtyfieldcomparison", value ? "" : null);
}

function containerHasDirtyFields(selector: JQuery<HTMLElement>, query = "[data-dirty=\"true\"]")
{
    // exclude elements contained within nested changeTrackingRoots
    return selector.find(query).not(selector.find(".js-changeTrackingRoot").find(query)).length > 0;
}

function overrideDirtyState(selectorArg: JQueryInput, override: boolean | undefined)
{
    const selector = $(selectorArg);
    if (typeof override === "undefined")
    {
        selector.removeData("dirty-override");
    }
    else
    {
        selector.data("dirty-override", override);
        selector.trigger("notifyDirty");
    }
}

function clearDirtyListInternal(selectorArg: JQuery<HTMLElement>, resetValues: boolean)
{
    if (resetValues)
    {
        resetInputValues(selectorArg);
    }
    selectorArg.find("[data-dirty='true']").attr("data-dirty", "false");
    return Promise.all(getHtmlViewers(selectorArg).toArray().map(function (iframe)
    {
        return skyward.iframeListener.clearDirtyList(iframe);
    }));
}

function resetInputValues(selectorArg: JQuery<HTMLElement>)
{
    const elements = selectorArg.find("[data-dirty='true']");
    elements.each(function (this: HTMLElement)
    {
        let element = $(this);
        // The inner input of a Vue custom element may hold data-dirty during keyup tracking,
        // but data-original-value lives on the parent custom element.
        if (!element.is("[data-original-value]"))
        {
            const parent = element.closest("[data-original-value]");
            if (parent.length)
            {
                element = parent;
            }
        }
        const originalValue = getOriginalValue(element);
        setValue(element, originalValue);
    });
}

function toggleDovDirtyState(dov: JQuery<HTMLElement>, element: JQuery<HTMLElement>, dirty: boolean)
{
    const dovId = dov.attr("id")!;

    if (dirty || globalDirtyOverride || dov.data("dirty-override"))
    {
        enableButtons(dovId);
    }
    else if (!element.is("[data-original-value-invalid]"))
    {
        removeFieldValidation();

        const isInWorkflow = dov.is(".workflowContent");
        const selector = isInWorkflow
            ? ":is([data-dirty=\"true\"],[data-original-value-invalid])"
            : "[data-dirty=\"true\"]";

        if (!containerHasDirtyFields(dov, selector))
        {
            disableButtons(dovId);
            removeDovValidation();
        }
    }

    function removeDovValidation()
    {
        dov.find(classnames.errorControl.dot()).removeClass(classnames.errorControl).removeAttr("aria-invalid");
        dov.find(classnames.errorText.dot()).not(classnames.persist.dot()).removeClass(classnames.errorText);
        dov.find(classnames.warningControl.dot()).removeClass(classnames.warningControl);
        dov.find(classnames.warningText.dot()).not(classnames.persist.dot()).removeClass(classnames.warningText);

        dov.find(".currentRecordContentBaseError").not(".persist").empty();
        dov.find(".warning-list").empty();
        dov.find(".help").not(classnames.persist.dot()).empty();

        const contentMessageArea = getContentMessageArea(dov.parent());
        contentMessageArea?.resetValidation();
    }

    function removeFieldValidation()
    {
        const contentMessageArea = getContentMessageArea(dov.parent());

        const validationName = element.attr("name");
        if (!validationName)
        {
            return;
        }
        const visualElements = getControl(validationName, dov);

        const wasError = visualElements.hasClass(classnames.errorControl);
        const wasWarning = visualElements.hasClass(classnames.warningControl);
        if (!wasError && !wasWarning)
        {
            return;
        }

        visualElements.removeClass([classnames.errorControl, classnames.warningControl]);
        setAriaInvalid(visualElements, false);
        const helpFields = getHelpMessage(validationName, dov).find(`.${classnames.errorText},.${classnames.warningText}`).not(classnames.persist.dot());
        helpFields.removeClass([classnames.errorText, classnames.warningText]).empty();
        const labelFields = getLabel(validationName, dov).filter(`.${classnames.errorText},.${classnames.warningText}`).not(classnames.persist.dot());
        labelFields.removeClass([classnames.errorText, classnames.warningText]);

        if (contentMessageArea)
        {
            contentMessageArea.modifyValidationCount(wasError ? -1 : 0, wasWarning ? -1 : 0);
        }
    }
}

function stringify(value: unknown)
{
    if (value === undefined || value === null)
    {
        return value;
    }
    return (typeof value === "string") ? value : JSON.stringify(value);
}

function normalizeNumberString(rawValue: unknown)
{
    const stringified = stringify(rawValue);
    if (stringified === undefined || stringified === null)
    {
        return stringified;
    }

    let numPrefix = "";
    let value = stringified.replace(/,/g, "").trim();

    if (value.startsWith("-"))
    {
        value = value.slice(1);
        numPrefix = "-";
    }

    if (value === "." || value === "")
    {
        return "";
    }

    value = trimLeadingTrailingZeros(value);

    const splitDecimal = /^(\d*)(\.\d*)?$/;
    const parts = splitDecimal.exec(value);
    if (parts === null)
    {
        return "";
    }

    const reformatted = `${parts[1] || "0"}${parts[2] || "."}`;
    return numPrefix + reformatted;
}

function trimLeadingTrailingZeros(num: string)
{
    let startSlice = 0;
    let endSlice = num.length;

    while (num[startSlice] === "0")
    {
        startSlice++;
    }
    if (startSlice === endSlice)
    {
        return num;
    }

    if (num.includes("."))
    {
        while (num[endSlice - 1] === "0")
        {
            endSlice--;
        }
    }

    return num.slice(startSlice, endSlice);
}
